import { bookById } from "./canon.ts";

export interface VerseAddr {
  chapter: number;
  verse: number;
}

/** A named span of one book. Children partition the parent exactly, in order. */
export interface SectionNode {
  name: string;
  description?: string;
  start: VerseAddr;
  end: VerseAddr;
  children?: SectionNode[];
  /** True while the name/description are the mechanical fallback ("Genesis 12–23"), i.e. nobody has labelled it yet. */
  auto?: true;
}

/** Stable identity of a section inside a book: its verse range, e.g. "12:1-23:20". Labels attach to these. */
export const rangeKey = (n: Pick<SectionNode, "start" | "end">): string => `${n.start.chapter}:${n.start.verse}-${n.end.chapter}:${n.end.verse}`;

export const cmpAddr = (a: VerseAddr, b: VerseAddr): number => a.chapter - b.chapter || a.verse - b.verse;
export const sameAddr = (a: VerseAddr, b: VerseAddr): boolean => cmpAddr(a, b) === 0;
export const addrInRange = (a: VerseAddr, start: VerseAddr, end: VerseAddr): boolean => cmpAddr(start, a) <= 0 && cmpAddr(a, end) <= 0;

/** Human label for a span using en dashes: "Genesis 1–11", "Psalms 119:1–8", "Exodus 3:1–4:17". */
export function sectionLabel(bookId: number, start: VerseAddr, end: VerseAddr, chapterVerses: readonly number[]): string {
  const name = bookById(bookId).name;
  const wholeChapters = start.verse === 1 && end.verse === chapterVerses[end.chapter - 1];
  if (wholeChapters) {
    return start.chapter === end.chapter ? `${name} ${start.chapter}` : `${name} ${start.chapter}–${end.chapter}`;
  }
  if (start.chapter === end.chapter) {
    return start.verse === end.verse ? `${name} ${start.chapter}:${start.verse}` : `${name} ${start.chapter}:${start.verse}–${end.verse}`;
  }
  return `${name} ${start.chapter}:${start.verse}–${end.chapter}:${end.verse}`;
}

/** Index path from the book's top level down to the leaf containing `addr`; [] if none. */
export function leafPathFor(tree: readonly SectionNode[], addr: VerseAddr): number[] {
  const i = tree.findIndex((n) => addrInRange(addr, n.start, n.end));
  if (i < 0) return [];
  const node = tree[i]!;
  return node.children ? [i, ...leafPathFor(node.children, addr)] : [i];
}

export function sectionAt(tree: readonly SectionNode[], path: readonly number[]): SectionNode | undefined {
  let level: readonly SectionNode[] | undefined = tree;
  let node: SectionNode | undefined;
  for (const i of path) {
    node = level?.[i];
    if (!node) return undefined;
    level = node.children;
  }
  return node;
}
